/** Drawing an editor draft onto a 2D canvas.
 *
 * Not the world renderer. That one draws terrain out of a 4096-key atlas
 * through a corner-mask composite, needs a `GameState` and an `Atlas` on the
 * GPU, and takes a frame to rebuild - which is the right trade for a map you
 * are playing and the wrong one for a map you are painting, where the brush has
 * to keep up with the pointer.
 *
 * What this draws instead is one cell, one tile: the terrain's own fill frame
 * out of the same packed sheet the world renderer samples, blitted at the zoom
 * the author is working at, with no corner masks and no transitions. So the
 * board is the game's ground rather than a diagram of it, and the cell
 * boundaries stay hard - which is what an author painting cells needs to see.
 *
 * WHICH of the sixteen fill frames a cell gets is `editorFillVariant`, a hash
 * of the cell and not the renderer's `(ty mod 4, tx mod 4)`. Drawing the game's
 * tiles fixed the board's COLOUR and left it wallpaper: the positional rule
 * repeats every four cells, and where the renderer hides that under a decal
 * scatter this board draws nothing over the ground at all. Measured on a 48x48
 * board of grass at 32px a cell, the row autocorrelation was +0.999 at a lag of
 * 128px - the four-cell period exactly - against +0.283 for the game's own
 * grass. `ground.ts` carries the argument and `test/editor-ground.spec.ts` the
 * guard.
 *
 * ## The colour, and why it moved
 *
 * This file used to paint flat colour out of `TERRAIN_MINIMAP_COLOUR`. That
 * table is correct for a minimap and wrong here, by a factor of 2.1 on
 * `(R+G+B)/3` for grass; the whole argument is in the header of `ground.ts`.
 * Flat colour is still the fallback - before the sheet has decoded, and at a
 * cell too small to carry a 64px tile - but it is now `TERRAIN_GROUND_COLOUR`,
 * measured off the art the game actually renders.
 *
 * Objects and starts are drawn as marks over the top rather than as sprites:
 * at eight pixels a cell a Skyclaw Aerie and a Windmill are the same brown
 * smudge, and what the author needs to know is "an object is here, and it is
 * mine".
 */

import type { TerrainKind } from "@game/core";
import { TERRAINS } from "@game/core";

import { PALETTE, playerColour } from "../theme/tokens.js";
import {
  FILL_PERIOD, TERRAIN_GROUND_COLOUR, editorFillVariant, terrainFillKey,
} from "./ground.js";
import type { DraftObject, DraftPlayer, EditorDraft } from "./model.js";
import { cellAt } from "./model.js";

/**
 * Cell sizes the zoom control offers, in device-independent pixels.
 *
 * Every step is a whole ratio of the 64px source tile - 16:1, 8:1, 4:1, 2:1,
 * 1:1 - for the reason `theme/atlas.ts` gives at length: pixel art shrunk by
 * 3.7x shimmers and shrunk by exactly 4x it does not. The old ladder had 6, 12
 * and 24 in it, which were fine for flat colour and are not fine now that the
 * board carries the art.
 */
export const ZOOM_STEPS: readonly number[] = Object.freeze([4, 8, 16, 32, 64]);
export const DEFAULT_ZOOM = 16;

/**
 * A terrain's flat colour, and a visible fallback rather than a crash.
 *
 * This table is built at module load, so a terrain with no entry used to throw
 * while the module was still initialising - which took down every screen that
 * imports `@game/ui`, not just the editor. Two terrains were added to the
 * kernel in one afternoon and that is exactly what happened. Magenta is a
 * deliberate choice for the fallback: it is not a colour any ground is, so a
 * new terrain shows up as one rather than hiding as a plausible brown.
 */
export const MISSING_TERRAIN_CSS = "rgb(255,0,220)";

const rgb = (kind: TerrainKind): string => {
  const c = TERRAIN_GROUND_COLOUR[kind] as readonly number[] | undefined;
  if (c === undefined) return MISSING_TERRAIN_CSS;
  return `rgb(${String(c[0])},${String(c[1])},${String(c[2])})`;
};

/** One colour per terrain, precomputed: this is called once per cell per frame. */
const TERRAIN_CSS: readonly string[] = TERRAINS.map(rgb);

/** The swatch a terrain is painted with, for the palette beside the canvas. */
export const terrainSwatch = (kind: TerrainKind): string =>
  TERRAIN_CSS[TERRAINS.indexOf(kind)] ?? MISSING_TERRAIN_CSS;

/**
 * The packed sheet, and enough of the manifest to find one frame in it.
 *
 * Deliberately not `Atlas` from `theme/atlas.ts`: that type carries sheet URLs
 * and a manifest this file has no use for, and taking the narrow shape is what
 * lets a test hand in a stub. `image` is whatever the caller decoded; a caller
 * with nothing decoded passes `null` and gets flat colour.
 */
export interface GroundArt {
  readonly image: CanvasImageSource;
  frame(key: string): { readonly x: number; readonly y: number;
    readonly w: number; readonly h: number } | null;
}

/**
 * The smallest zoom step at which one source tile is not being shrunk below
 * legibility. Under this the board is painted flat, because a 64px tile of
 * grass at 4px a cell is one muddy pixel and sixteen variants of it are
 * sixteen slightly different muddy pixels - noise standing in for ground.
 */
const MIN_ART_CELL = 8;

/**
 * Every fill frame, by terrain and positional variant, built once per sheet.
 *
 * The board blits one tile a cell, so the naive shape - compose the key and
 * look it up inside the loop - builds and hashes a string per cell per frame:
 * 1518 of them at the zoom the editor opens on, 5824 on a 144-cell board, on
 * every pointer move. This is 192 lookups instead, done when the sheet changes.
 *
 * Cached against the `GroundArt` object's identity rather than recomputed,
 * which is safe because `useGroundArt` memoises it on the decoded image and the
 * atlas. A caller that hands in a fresh object every call pays for a rebuild
 * and still gets the right answer.
 */
type Frame = { readonly x: number; readonly y: number;
  readonly w: number; readonly h: number };
let frameTable: readonly (readonly (Frame | null)[])[] | null = null;
let frameTableArt: GroundArt | null = null;

function framesFor(art: GroundArt): readonly (readonly (Frame | null)[])[] {
  if (frameTableArt === art && frameTable !== null) return frameTable;
  const table = TERRAINS.map((t) => {
    const row: (Frame | null)[] = [];
    for (let v = 0; v < FILL_PERIOD * FILL_PERIOD; v++) {
      row.push(art.frame(terrainFillKey(t, v % FILL_PERIOD, Math.floor(v / FILL_PERIOD))));
    }
    return row;
  });
  frameTable = table;
  frameTableArt = art;
  return table;
}

export interface PaintView {
  /** Top-left cell of the visible window. */
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly cell: number;
}

/** The brush footprint under the pointer, so the author sees what he will paint. */
export interface PaintCursor {
  readonly x: number;
  readonly y: number;
  /** Disc radius in cells; 0 is a single cell. */
  readonly radius: number;
}

/**
 * Where the board's top-left corner sits inside the stage, in CSS pixels.
 *
 * A map smaller than the stage is centred rather than jammed into the corner.
 * The first draft did jam it, and a 48-cell board at eight pixels a cell read
 * as a rendering failure: a small green square in the top-left of a large black
 * panel, with no way to tell whether the rest had failed to draw.
 *
 * Both the painter and the hit test go through this, because an offset applied
 * to one and not the other is a board you cannot click where you can see it.
 */
export function viewOrigin(
  d: { width: number; height: number }, view: PaintView, widthPx: number, heightPx: number,
): { readonly px: number; readonly py: number } {
  const mapW = d.width * view.cell;
  const mapH = d.height * view.cell;
  return {
    px: mapW < widthPx ? Math.floor((widthPx - mapW) / 2) : 0,
    py: mapH < heightPx ? Math.floor((heightPx - mapH) / 2) : 0,
  };
}

/**
 * The largest zoom step at which the whole board fits the stage.
 *
 * Kept because the Board card offers it as a button: after a resize, or after
 * working close in on one corner, "show me all of it" is one press.
 */
export function fitZoom(
  d: { width: number; height: number }, widthPx: number, heightPx: number,
): number {
  if (widthPx <= 0 || heightPx <= 0) return DEFAULT_ZOOM;
  let best = ZOOM_STEPS[0] ?? DEFAULT_ZOOM;
  for (const c of ZOOM_STEPS) {
    if (d.width * c <= widthPx && d.height * c <= heightPx) best = c;
  }
  return best;
}

/**
 * The smallest zoom step at which the board COVERS the stage, and the largest
 * step if none of them does.
 *
 * This is what the editor opens on, and it is a reversal. It used to open on
 * `fitZoom`, on the argument that the first thing an author does after changing
 * the size is look at the whole map. That argument was right and the cost of it
 * was not seen: a 48-cell board fits a 1472x1024 stage at 16px a cell, which is
 * a 768px square of ground floating in a 1472x1024 field of near-black. Counted
 * as 32x32 blocks with luminance sd < 4, that surround was 825 blocks - 41.7
 * points of the editor's 47.4% blank surface, on a build whose twelve town
 * screens run 0.2-2.1%.
 *
 * The whole-map view did not go away, it moved: the left rail carries a minimap
 * of the draft, which is where a map editor has always put it, and clicking it
 * moves the window. `fitZoom` is still one button away.
 */
export function coverZoom(
  d: { width: number; height: number }, widthPx: number, heightPx: number,
): number {
  if (widthPx <= 0 || heightPx <= 0) return DEFAULT_ZOOM;
  const largest = ZOOM_STEPS[ZOOM_STEPS.length - 1] ?? DEFAULT_ZOOM;
  for (const c of ZOOM_STEPS) {
    if (d.width * c >= widthPx && d.height * c >= heightPx) return c;
  }
  return largest;
}

// ------------------------------------------------------------- the surround

/**
 * The cut stone the board is set into, as a repeating ashlar pattern.
 *
 * What was here was `#12100c`, one flat value across the whole stage, and a
 * flat value is exactly what a critic reads as a dev page: at the zoom the
 * editor used to open on, that surround was 825 of the frame's 1980 32x32
 * blocks.
 *
 * Grit alone does not fix it and should not be reached for - a speckle chosen
 * to clear a variance threshold is a metric being gamed, not a surface. This is
 * masonry: 64x32 stones in running bond, each face graded across its diagonal
 * away from the same upper-left light every other screen in this build is lit
 * by, a lit top edge, a shadowed bottom edge, and dark mortar between. It is
 * the `PALETTE.stone0/1/2` ramp and nothing else, so the floor is the same
 * material as the ground of every other screen.
 *
 * Built once into a 128x64 pattern and cached, because it is drawn on every
 * pointer move: a floor laid down as a couple of thousand `fillRect`s per frame
 * is a millisecond the brush does not have. The grit is a 32-bit hash of the
 * pixel pair rather than `Math.random`, so two paints of the same board are the
 * same image and a screenshot diff means something.
 */
let stonePattern: CanvasPattern | null = null;
let stoneOwner: CanvasRenderingContext2D | null = null;

const hash2 = (x: number, y: number): number => {
  let h = (x * 374761393 + y * 668265263) | 0;
  h = (h ^ (h >>> 13)) * 1274126177;
  return (h ^ (h >>> 16)) >>> 0;
};

/** Stone width, stone height, and the offset of every other course. */
const ASHLAR_W = 64;
const ASHLAR_H = 32;

function stoneFill(ctx: CanvasRenderingContext2D): CanvasPattern | string {
  if (stonePattern !== null && stoneOwner === ctx) return stonePattern;
  // No DOM in a test: the flat stone is the right degradation, and the
  // geometry the tests actually assert on is unaffected by it.
  if (typeof document === "undefined") return PALETTE.stone0;
  const w = ASHLAR_W * 2, h = ASHLAR_H * 2;
  const tile = document.createElement("canvas");
  tile.width = w;
  tile.height = h;
  const t = tile.getContext("2d");
  if (t === null) return PALETTE.stone0;
  const img = t.createImageData(w, h);
  const px = img.data;

  // The two ends of the face ramp, and the mortar. Lit corner first.
  // Dim on purpose. The floor is the thing the board is set INTO, and the
  // first pass at these values ran a face mean of 31.8 against the grass the
  // board paints at 52.1 - close enough that the surround read as the subject
  // and the map as a tile on top of it. These run about 20.
  const lit = [36, 32, 25];
  const dark = [13, 11, 7];
  const mortar = [7, 6, 4];

  for (let y = 0; y < h; y++) {
    const course = Math.floor(y / ASHLAR_H);
    const shift = course % 2 === 0 ? 0 : ASHLAR_W / 2;
    const sy = y % ASHLAR_H;
    for (let x = 0; x < w; x++) {
      const sx = (((x - shift) % ASHLAR_W) + ASHLAR_W) % ASHLAR_W;
      const i = (y * w + x) * 4;
      let r: number, g: number, b: number;
      if (sx === 0 || sy === 0) {
        [r, g, b] = mortar as [number, number, number];
      } else {
        // Graded across the stone's own diagonal, then the two struck edges.
        const k = 1 - (sx / ASHLAR_W + sy / ASHLAR_H) / 2;
        r = dark[0]! + (lit[0]! - dark[0]!) * k;
        g = dark[1]! + (lit[1]! - dark[1]!) * k;
        b = dark[2]! + (lit[2]! - dark[2]!) * k;
        if (sy === 1 || sx === 1) { r += 11; g += 10; b += 7; }
        if (sy === ASHLAR_H - 1 || sx === ASHLAR_W - 1) { r *= 0.55; g *= 0.55; b *= 0.55; }
      }
      // Grit, so a face is quarried rather than airbrushed.
      const rand = hash2(x, y) % 1000;
      if (rand < 40) { r += 13; g += 11; b += 9; }
      else if (rand < 150) { r -= 4; g -= 3; b -= 2; }
      px[i] = Math.max(0, Math.min(255, Math.round(r)));
      px[i + 1] = Math.max(0, Math.min(255, Math.round(g)));
      px[i + 2] = Math.max(0, Math.min(255, Math.round(b)));
      px[i + 3] = 255;
    }
  }
  t.putImageData(img, 0, 0);
  const pattern = t.createPattern(tile, "repeat");
  if (pattern === null) return PALETTE.stone0;
  stonePattern = pattern;
  stoneOwner = ctx;
  return pattern;
}

/** The stone floor plus the light that falls on it, in device pixels. */
function paintSurround(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  ctx.fillStyle = stoneFill(ctx);
  ctx.fillRect(0, 0, w, h);
  // The build's light comes from the upper left on every other screen.
  const warm = ctx.createLinearGradient(0, 0, w * 0.75, h);
  // Light enough to read as a light, weak enough to leave the masonry legible:
  // at 0.34 in the far corner the courses went flat and the floor was a dark
  // rectangle again, which is the defect this is here to fix.
  warm.addColorStop(0, "rgba(140,114,70,0.20)");
  warm.addColorStop(0.55, "rgba(60,48,30,0.04)");
  warm.addColorStop(1, "rgba(0,0,0,0.18)");
  ctx.fillStyle = warm;
  ctx.fillRect(0, 0, w, h);
}

/**
 * The carved lip the board sits in: black keyline, gilt hairline, dark shadow
 * cast inwards onto the ground.
 *
 * Drawn only where there is surround to draw it on. A board that covers the
 * stage has no lip to show and gets the inner shadow alone, which is what seats
 * it against the panel edge.
 */
function paintWell(
  ctx: CanvasRenderingContext2D, px: number, py: number, w: number, h: number,
): void {
  ctx.save();
  ctx.lineJoin = "miter";
  // Shadow the stone takes from the raised edge, outside the board.
  ctx.strokeStyle = "rgba(0,0,0,0.55)";
  ctx.lineWidth = 6;
  ctx.strokeRect(px - 3, py - 3, w + 6, h + 6);
  ctx.strokeStyle = PALETTE.gold1;
  ctx.lineWidth = 2;
  ctx.strokeRect(px - 2, py - 2, w + 4, h + 4);
  ctx.strokeStyle = PALETTE.gold2;
  ctx.lineWidth = 1;
  ctx.strokeRect(px - 0.5, py - 0.5, w + 1, h + 1);
  ctx.restore();
}

// ---------------------------------------------------------------- the board

/**
 * Paint the whole visible window.
 *
 * `ctx` is expected to be untransformed; this draws in device pixels and takes
 * the ratio, because a canvas scaled with CSS at these cell sizes turns every
 * grid line into a grey blur.
 */
export function paintDraft(
  ctx: CanvasRenderingContext2D, d: EditorDraft, view: PaintView,
  widthPx: number, heightPx: number, ratio = 1,
  art: GroundArt | null = null, cursor: PaintCursor | null = null,
): void {
  const cell = view.cell * ratio;
  const cols = Math.ceil(widthPx / view.cell) + 1;
  const rows = Math.ceil(heightPx / view.cell) + 1;
  const origin = viewOrigin(d, view, widthPx, heightPx);

  ctx.save();
  ctx.imageSmoothingEnabled = false;
  paintSurround(ctx, widthPx * ratio, heightPx * ratio);
  ctx.translate(origin.px * ratio, origin.py * ratio);

  const tiled = art !== null && view.cell >= MIN_ART_CELL;
  const table = tiled && art !== null ? framesFor(art) : null;
  for (let row = 0; row < rows; row++) {
    const cy = view.y + row;
    if (cy < 0 || cy >= d.height) continue;
    for (let col = 0; col < cols; col++) {
      const cx = view.x + col;
      if (cx < 0 || cx >= d.width) continue;
      const t = d.terrain[cellAt(d, cx, cy, view.z)] ?? 0;
      const dx = col * cell, dy = row * cell;
      const f = table === null ? null : table[t]?.[editorFillVariant(cx, cy)] ?? null;
      // The flat colour is the fallback, not an undercoat. Laying it down under
      // every tile as well is a second full-board pass for pixels no eye ever
      // sees; measured interleaved in the harness's browser at 1518 tiles,
      // 20.3ms with the undercoat against 19.6ms without.
      if (f === null || art === null) {
        ctx.fillStyle = TERRAIN_CSS[t] ?? "#000";
        ctx.fillRect(dx, dy, cell, cell);
      } else {
        ctx.drawImage(art.image, f.x, f.y, f.w, f.h, dx, dy, cell, cell);
      }
    }
  }

  // A grid, but only when a cell is large enough for it to be a grid rather
  // than a wash: at four pixels a cell the lines are half the picture.
  //
  // Two hairlines, not one. A single 20%-black line was legible over the
  // minimap's pale flattened colours and is not over the art, where half the
  // terrains are darker than 55 luminance; the pale line under it is the same
  // chamfer every plate in this build has, and it reads on dark ground.
  if (view.cell >= 12) {
    const gw = Math.min(cols * cell, (d.width - view.x) * cell);
    const gh = Math.min(rows * cell, (d.height - view.y) * cell);
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(0,0,0,0.30)";
    ctx.beginPath();
    for (let col = 0; col <= cols; col++) {
      const gx = Math.round(col * cell) + 0.5;
      ctx.moveTo(gx, 0); ctx.lineTo(gx, gh);
    }
    for (let row = 0; row <= rows; row++) {
      const gy = Math.round(row * cell) + 0.5;
      ctx.moveTo(0, gy); ctx.lineTo(gw, gy);
    }
    ctx.stroke();
    ctx.strokeStyle = "rgba(236,216,166,0.10)";
    ctx.beginPath();
    for (let col = 0; col <= cols; col++) {
      const gx = Math.round(col * cell) + 1.5;
      ctx.moveTo(gx, 0); ctx.lineTo(gx, gh);
    }
    for (let row = 0; row <= rows; row++) {
      const gy = Math.round(row * cell) + 1.5;
      ctx.moveTo(0, gy); ctx.lineTo(gw, gy);
    }
    ctx.stroke();
  }

  paintObjects(ctx, d, view, cols, rows, ratio);
  paintStarts(ctx, d, view, ratio);
  if (cursor !== null) paintCursor(ctx, cursor, view, ratio);
  ctx.restore();

  // The lip goes on last so nothing is drawn over it, and outside the
  // translate so it is in stage coordinates.
  ctx.save();
  paintWell(
    ctx, origin.px * ratio, origin.py * ratio,
    Math.min(d.width * cell, widthPx * ratio), Math.min(d.height * cell, heightPx * ratio),
  );
  ctx.restore();
}

/** The disc the brush will paint, outlined on the ground under the pointer. */
function paintCursor(
  ctx: CanvasRenderingContext2D, cursor: PaintCursor, view: PaintView, ratio: number,
): void {
  const cell = view.cell * ratio;
  const r = cursor.radius;
  const px = (cursor.x - r - view.x) * cell;
  const py = (cursor.y - r - view.y) * cell;
  const size = (r * 2 + 1) * cell;
  ctx.save();
  ctx.lineWidth = Math.max(1, Math.round(cell * 0.06));
  if (r === 0) {
    ctx.strokeStyle = "rgba(0,0,0,0.75)";
    ctx.strokeRect(px + 1, py + 1, cell - 2, cell - 2);
    ctx.strokeStyle = "rgba(244,224,163,0.85)";
    ctx.strokeRect(px + 0.5, py + 0.5, cell - 1, cell - 1);
  } else {
    ctx.beginPath();
    ctx.arc(px + size / 2, py + size / 2, size / 2 - 1, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(0,0,0,0.75)";
    ctx.lineWidth = Math.max(2, Math.round(cell * 0.1));
    ctx.stroke();
    ctx.strokeStyle = "rgba(244,224,163,0.85)";
    ctx.lineWidth = Math.max(1, Math.round(cell * 0.05));
    ctx.stroke();
  }
  ctx.restore();
}

/** A diamond per object; a ring round it when it is flagged to somebody. */
function paintObjects(
  ctx: CanvasRenderingContext2D, d: EditorDraft, view: PaintView,
  cols: number, rows: number, ratio: number,
): void {
  const cell = view.cell * ratio;
  const r = Math.max(2, cell * 0.32);
  for (const o of d.objects) {
    if (o.z !== view.z) continue;
    const col = o.x - view.x, row = o.y - view.y;
    if (col < -1 || row < -1 || col > cols || row > rows) continue;
    const cx = col * cell + cell / 2;
    const cy = row * cell + cell / 2;

    ctx.beginPath();
    ctx.moveTo(cx, cy - r);
    ctx.lineTo(cx + r, cy);
    ctx.lineTo(cx, cy + r);
    ctx.lineTo(cx - r, cy);
    ctx.closePath();
    ctx.fillStyle = o.owner === null ? "rgba(238,226,198,0.92)" : playerColour(ownerColour(d, o));
    ctx.fill();
    ctx.lineWidth = Math.max(1, cell * 0.08);
    ctx.strokeStyle = "rgba(12,10,8,0.85)";
    ctx.stroke();
  }
}

const ownerColour = (d: EditorDraft, o: DraftObject): string =>
  d.players.find((p) => p.id === o.owner)?.colour ?? "red";

/** A filled square for a capital, an open ring for the starting hero. */
function paintStarts(
  ctx: CanvasRenderingContext2D, d: EditorDraft, view: PaintView, ratio: number,
): void {
  const cell = view.cell * ratio;
  const at = (p: { x: number; y: number }): readonly [number, number] =>
    [(p.x - view.x) * cell, (p.y - view.y) * cell];

  for (const player of d.players) {
    if (player.kind === "none") continue;
    const colour = playerColour(player.colour);
    if (player.town !== null && player.town.z === view.z) {
      const [px, py] = at(player.town);
      const size = Math.max(4, cell * 0.9);
      ctx.fillStyle = colour;
      ctx.fillRect(px + (cell - size) / 2, py + (cell - size) / 2, size, size);
      ctx.lineWidth = Math.max(1, cell * 0.12);
      ctx.strokeStyle = "#141008";
      ctx.strokeRect(px + (cell - size) / 2, py + (cell - size) / 2, size, size);
    }
    if (player.hero !== null && player.hero.z === view.z) {
      const [px, py] = at(player.hero);
      ctx.beginPath();
      ctx.arc(px + cell / 2, py + cell / 2, Math.max(2, cell * 0.34), 0, Math.PI * 2);
      ctx.lineWidth = Math.max(1.5, cell * 0.18);
      ctx.strokeStyle = colour;
      ctx.stroke();
      ctx.lineWidth = Math.max(1, cell * 0.06);
      ctx.strokeStyle = "#141008";
      ctx.stroke();
    }
  }
}

// -------------------------------------------------------------- the minimap

/**
 * The whole level at a fixed number of texels a cell, drawn into an image the
 * rail scales up.
 *
 * This is the view the board gave up when it stopped opening on `fitZoom`, and
 * it is a better one: a map editor's navigator has always been a small
 * always-there picture of the whole thing rather than the working surface
 * zoomed out to uselessness. It is always visible, it does not change scale
 * when the author works close in, and clicking it moves the window instead of
 * changing what the brush is pointing at.
 *
 * The image is a fixed texel grid rather than something fitted to the panel, so
 * there is no letterboxing to paint round and the panel can be sized to the
 * map's own aspect. Two texels a cell rather than one, so a capital is a mark
 * and not a dot; 252 cells - the largest board - is a 504px image, which
 * encodes in well under a millisecond.
 */
export const MINIMAP_TEXEL = 2;

export const minimapSize = (
  d: { width: number; height: number },
): { readonly w: number; readonly h: number } => ({
  w: d.width * MINIMAP_TEXEL,
  h: d.height * MINIMAP_TEXEL,
});

export function paintMinimap(
  ctx: CanvasRenderingContext2D, d: EditorDraft, z: number,
  window: { readonly x: number; readonly y: number;
    readonly cols: number; readonly rows: number } | null = null,
): void {
  const s = MINIMAP_TEXEL;
  ctx.save();
  ctx.imageSmoothingEnabled = false;

  for (let y = 0; y < d.height; y++) {
    for (let x = 0; x < d.width; x++) {
      const t = d.terrain[cellAt(d, x, y, z)] ?? 0;
      ctx.fillStyle = TERRAIN_CSS[t] ?? "#000";
      ctx.fillRect(x * s, y * s, s, s);
    }
  }

  for (const o of d.objects) {
    if (o.z !== z) continue;
    ctx.fillStyle = o.owner === null
      ? "rgba(238,226,198,0.92)" : playerColour(ownerColour(d, o));
    ctx.fillRect(o.x * s, o.y * s, s, s);
  }
  for (const player of d.players) {
    if (player.kind === "none") continue;
    if (player.town !== null && player.town.z === z) {
      ctx.fillStyle = playerColour(player.colour);
      ctx.fillRect(player.town.x * s - s, player.town.y * s - s, s * 3, s * 3);
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#141008";
      ctx.strokeRect(player.town.x * s - s + 0.5, player.town.y * s - s + 0.5, s * 3 - 1, s * 3 - 1);
    }
    if (player.hero !== null && player.hero.z === z) {
      ctx.fillStyle = "#141008";
      ctx.fillRect(player.hero.x * s - 1, player.hero.y * s - 1, s + 2, s + 2);
      ctx.fillStyle = playerColour(player.colour);
      ctx.fillRect(player.hero.x * s, player.hero.y * s, s, s);
    }
  }

  if (window !== null) {
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(0,0,0,0.85)";
    ctx.strokeRect(
      window.x * s - 0.5, window.y * s - 0.5, window.cols * s + 1, window.rows * s + 1,
    );
    ctx.strokeStyle = "rgba(246,232,186,0.92)";
    ctx.strokeRect(window.x * s + 0.5, window.y * s + 0.5, window.cols * s - 1, window.rows * s - 1);
  }
  ctx.restore();
}

/**
 * Which cell a click at these box-relative pixels is over.
 *
 * Takes the box's own size rather than the image's, because the image is scaled
 * by CSS: the navigator has to be clickable where it is seen, which is the same
 * rule `viewOrigin` states for the board.
 */
export function minimapCell(
  d: { width: number; height: number }, boxW: number, boxH: number,
  offsetX: number, offsetY: number,
): { readonly x: number; readonly y: number } {
  if (boxW <= 0 || boxH <= 0) return { x: 0, y: 0 };
  return {
    x: Math.max(0, Math.min(d.width - 1, Math.floor((offsetX / boxW) * d.width))),
    y: Math.max(0, Math.min(d.height - 1, Math.floor((offsetY / boxH) * d.height))),
  };
}

// ------------------------------------------------------------------ hit test

/** Which cell a pointer at these canvas-relative pixels is over. */
export function cellFromPixel(
  d: { width: number; height: number }, view: PaintView,
  offsetX: number, offsetY: number, widthPx: number, heightPx: number,
): { readonly x: number; readonly y: number } {
  const origin = viewOrigin(d, view, widthPx, heightPx);
  return {
    x: view.x + Math.floor((offsetX - origin.px) / view.cell),
    y: view.y + Math.floor((offsetY - origin.py) / view.cell),
  };
}

/** Keep the window inside the map, allowing for a map smaller than the canvas. */
export function clampView(
  view: PaintView, d: EditorDraft, widthPx: number, heightPx: number,
): PaintView {
  const cols = Math.floor(widthPx / view.cell);
  const rows = Math.floor(heightPx / view.cell);
  const maxX = Math.max(0, d.width - cols);
  const maxY = Math.max(0, d.height - rows);
  return {
    ...view,
    x: Math.max(0, Math.min(view.x, maxX)),
    y: Math.max(0, Math.min(view.y, maxY)),
    z: Math.max(0, Math.min(view.z, d.levels - 1)),
  };
}

/**
 * Put `cell` in the middle of the window, clamped to the board.
 *
 * The minimap and the "show me this error" jump both go through here, so a
 * navigator click and a jump land the same way.
 */
export function centreOn(
  view: PaintView, d: EditorDraft, widthPx: number, heightPx: number,
  cx: number, cy: number,
): PaintView {
  const cols = Math.floor(widthPx / view.cell);
  const rows = Math.floor(heightPx / view.cell);
  return clampView(
    { ...view, x: cx - Math.floor(cols / 2), y: cy - Math.floor(rows / 2) },
    d, widthPx, heightPx,
  );
}

/** Player colours as CSS, for the panels beside the canvas. */
export const draftPlayerColour = (p: DraftPlayer): string => playerColour(p.colour);

/** How many whole cells the window shows, for the minimap's rectangle. */
export const visibleCells = (
  view: PaintView, widthPx: number, heightPx: number,
): { readonly cols: number; readonly rows: number } => ({
  cols: Math.max(1, Math.floor(widthPx / view.cell)),
  rows: Math.max(1, Math.floor(heightPx / view.cell)),
});

/** Cell counts per terrain on one level, for the mix bar in the rail. */
export function terrainCensus(d: EditorDraft, z: number): readonly number[] {
  const counts = new Array<number>(TERRAINS.length).fill(0);
  for (let y = 0; y < d.height; y++) {
    for (let x = 0; x < d.width; x++) {
      const t = d.terrain[cellAt(d, x, y, z)] ?? 0;
      counts[t] = (counts[t] ?? 0) + 1;
    }
  }
  return counts;
}
